import React, { useCallback, useMemo, useState } from 'react';
import {
    riskImpactScore,
    riskLikelihoodScore,
    riskScoreColor
} from '../../../constants/AppEnums';
import {
    ArrowDownTrayIcon,
    ChevronDownIcon,
    EyeIcon,
    PencilSquareIcon,
    PlusIcon,
    TrashIcon,
    ShieldExclamationIcon,
    UserPlusIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../../ui';
import PermissionGate from '../../PermissionGate';
import { DataTable } from '../../common/DataTable';
import {
    assignRiskApi,
    deleteCustomerRiskApi,
    deleteRiskApi,
    saveCustomerRiskApi,
    saveRiskApi,
    updateCustomerRiskApi,
    updateRiskApi,
} from '../../../services/axios/AuthApis/RisksManagementApis';
import RiskForm from './RiskForm';
import AssignRiskModal from './AssignRiskModal';
import RiskViewModal from './RiskViewModal';
import { RiskDeleteConfirmationModal } from './RiskDeleteConfirmationModal';
import { useSelector } from 'react-redux';

const RiskManagementList = ({
    risks,
    searchTerm,
    setSearchTerm,
    filterStatus,
    setFilterStatus,
    filterSeverity,
    setFilterSeverity,
    textColumnFilters,
    setTextColumnFilters,
    getRiskList,
    totalRiskCount,
    showingCount,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    userRole,
    onExport,
    sorting,
    onSortingChange,
    RiskDataloading,
}) => {
    const [showForm, setShowForm] = useState(false);
    const [editingRisk, setEditingRisk] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const totalPages = Math.max(1, Math.ceil(totalRiskCount / itemsPerPage));
    const [selectedRisk, setSelectedRisk] = useState();
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const userDetail = useSelector((state) => state.user);
    const customerMasterId = userDetail?.user?.customer_master_id || '';
    const columnFilters = [
        ...Object.entries(textColumnFilters || {})
            .filter(([, value]) => value)
            .map(([id, value]) => ({ id, value })),
        ...(filterStatus ? [{ id: 'risk_status', value: filterStatus }] : []),
        ...(filterSeverity ? [{ id: 'risk_score', value: filterSeverity }] : []),
    ];

    const handlePageChange = (page, pageSize) => {
        if (pageSize !== undefined && pageSize !== itemsPerPage) {
            setItemsPerPage(pageSize);
            setCurrentPage(1);
            return;
        }
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            getRiskList(page);
        }
    };

    const handleAddNew = () => { setEditingRisk(null); setIsEditing(false); setShowForm(true); };
    const handleEditRisk = useCallback((risk) => { setEditingRisk(risk); setIsEditing(true); setShowForm(true); }, []);
    const handleAssign = useCallback((risk) => { setSelectedRisk(risk.id); setShowAssignModal(true); }, []);
    const handleViewRisk = useCallback((risk) => { setShowViewModal(true); setEditingRisk(risk); setIsEditing(false); }, []);
    const handleDeleteRisk = useCallback((risk) => { setShowDeleteModal(true); setEditingRisk(risk); setIsEditing(false); }, []);

    const uniqueFrameworks = useMemo(
        () => [...new Set((risks || []).map((r) => r.framework).filter(Boolean))],
        [risks]
    );
    const uniqueCategories = useMemo(
        () => [...new Set((risks || []).map((r) => r.category).filter(Boolean))],
        [risks]
    );
    const uniqueSubCategories = useMemo(
        () => [...new Set((risks || []).map((r) => r.risk_sub_category).filter(Boolean))],
        [risks]
    );

    const columns = useMemo(() => {
        const riskColumns = [
            {
                accessorKey: 'risk_id',
                header: 'Risk ID',
                meta: {
                    filter: {
                        variant: 'text',
                        label: 'Risk ID',
                        placeholder: 'Filter risk ID...',
                    },
                },
                cell: ({ getValue }) => (
                    <span className="whitespace-nowrap text-sm font-semibold text-gray-900">
                        {getValue()}
                    </span>
                ),
                size: 130,
            },
            {
                accessorKey: 'framework',
                header: 'Controls',
                meta: {
                    filter: {
                        variant: 'combobox',
                        label: 'Controls',
                        placeholder: 'fliter controls',
                        options: uniqueFrameworks.map((v) => ({ value: v, label: v })),
                        maxVisible: 4,
                    },
                },
                cell: ({ getValue }) => (
                    <span className="block max-w-[140px] text-sm text-gray-700" title={getValue() || ''}>
                        {getValue()}
                    </span>
                ),
                size: 160,
            },
            {
                accessorKey: 'category',
                header: 'Category',
                meta: {
                    filter: {
                        variant: 'combobox',
                        label: 'Category',
                        placeholder: 'All categories',
                        options: uniqueCategories.map((v) => ({ value: v, label: v })),
                        maxVisible: 4,
                    },
                },
                cell: ({ getValue }) => (
                    <span className="block max-w-[140px] text-sm text-gray-700" title={getValue() || ''}>
                        {getValue()}
                    </span>
                ),
                size: 160,
            },
            {
                accessorKey: 'risk_sub_category',
                header: 'Sub Category',
                meta: {
                    filter: {
                        variant: 'combobox',
                        label: 'Sub Category',
                        placeholder: 'All sub categories',
                        options: uniqueSubCategories.map((v) => ({ value: v, label: v })),
                        maxVisible: 4,
                    },
                },
                cell: ({ getValue }) => (
                    <span className="block max-w-[140px] text-sm text-gray-700" title={getValue() || ''}>
                        {getValue()}
                    </span>
                ),
                size: 160,
            },
            {
                accessorKey: 'risk_description',
                header: 'Description',
                meta: {
                    filter: {
                        variant: 'text',
                        label: 'Description',
                        placeholder: 'Filter description...',
                    },
                },
                cell: ({ getValue }) => (
                    <span className="block max-w-[300px] text-sm text-gray-600" title={getValue() || ''}>
                        {getValue()}
                    </span>
                ),
                size: 320,
            },
        ];

        if (userRole !== 'admin') {
            riskColumns.push(
                {
                    id: 'impact',
                    accessorFn: (risk) => risk.inherent_impact_type,
                    header: 'Impact',
                    meta: {
                        filter: {
                            variant: 'select',
                            label: 'Impact',
                            placeholder: 'All impacts',
                            options: riskImpactScore.map(({ name, value }) => ({
                                value: name,
                                label: `${name.charAt(0).toUpperCase()}${name.slice(1)} (${value})`,
                            })),
                        },
                    },
                    cell: ({ row }) => {
                        const impact = row.original.inherent_impact_type;
                        const impactObj = riskImpactScore.find(
                            (item) => item.name.toLowerCase() === impact?.toLowerCase(),
                        );
                        return (
                            <span className="inline-flex whitespace-nowrap rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                                {impact} – {impactObj?.value ?? '—'}
                            </span>
                        );
                    },
                },
                {
                    id: 'likelihood',
                    accessorFn: (risk) => risk.inherent_likelihood_type,
                    header: 'Likelihood',
                    meta: {
                        filter: {
                            variant: 'select',
                            label: 'Likelihood',
                            placeholder: 'All likelihoods',
                            options: riskLikelihoodScore.map(({ name, value }) => ({
                                value: name,
                                label: `${name.charAt(0).toUpperCase()}${name.slice(1)} (${value})`,
                            })),
                        },
                    },
                    cell: ({ row }) => {
                        const likelihood = row.original.inherent_likelihood_type;
                        const likelihoodObj = riskLikelihoodScore.find(
                            (item) => item.name.toLowerCase() === likelihood?.toLowerCase(),
                        );
                        return (
                            <span className="inline-flex whitespace-nowrap rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                                {likelihood} – {likelihoodObj?.value ?? '—'}
                            </span>
                        );
                    },
                },
                {
                    id: 'risk_score',
                    accessorFn: (risk) => risk.inherent_risk_score,
                    header: 'Risk Score',
                    meta: {
                        filter: {
                            variant: 'select',
                            label: 'Risk score',
                            placeholder: 'All scores',
                            options: [
                                { value: 'extreme', label: 'Extreme' },
                                { value: 'significant', label: 'Significant' },
                                { value: 'moderate', label: 'Moderate' },
                                { value: 'low', label: 'Low' },
                            ],
                        },
                    },
                    cell: ({ row }) => {
                        const risk = row.original;
                        const impactObj = riskImpactScore.find(
                            (item) => item.name.toLowerCase() === risk.inherent_impact_type?.toLowerCase(),
                        );
                        const likelihoodObj = riskLikelihoodScore.find(
                            (item) => item.name.toLowerCase() === risk.inherent_likelihood_type?.toLowerCase(),
                        );
                        const scoreColorObj = riskScoreColor.find(
                            (item) => item.name.toLowerCase() === risk.inherent_risk_score?.toLowerCase(),
                        );
                        const scoreValue = impactObj?.value && likelihoodObj?.value
                            ? impactObj.value + likelihoodObj.value
                            : '—';
                        return (
                            <span className={`inline-flex whitespace-nowrap items-center rounded-lg px-2.5 py-1 text-xs font-bold text-white ${scoreColorObj?.color ?? 'bg-gray-400'}`}>
                                {risk.inherent_risk_score} – {scoreValue}
                            </span>
                        );
                    },
                },
            );
        }

        riskColumns.push(
            {
                accessorKey: 'created_by',
                header: 'Created By',
                meta: {
                    filter: {
                        variant: 'text',
                        label: 'Created By',
                        placeholder: 'Filter creator...',
                    },
                },
                cell: ({ getValue }) => (
                    <span className="break-words text-sm text-gray-600">{getValue() || 'System'}</span>
                ),
            },
        );

        if (userRole !== 'admin') {
            riskColumns.push({
                accessorKey: 'risk_status',
                header: 'Status',
                meta: {
                    filter: {
                        variant: 'select',
                        label: 'Status',
                        placeholder: 'All statuses',
                        options: [
                            { value: 'open', label: 'Open' },
                            { value: 'mitigated', label: 'Mitigated' },
                            { value: 'closed', label: 'Closed' },
                            { value: 'in-progress', label: 'In Progress' },
                        ],
                    },
                },
                cell: ({ getValue }) => {
                    const status = getValue();
                    const statusStyle = {
                        open: 'bg-red-100 text-red-700',
                        mitigated: 'bg-green-100 text-green-700',
                        closed: 'bg-gray-100 text-gray-600',
                        'in-progress': 'bg-primary-50 text-primary-700',
                    }[status?.toLowerCase()] ?? 'bg-gray-100 text-gray-600';
                    return (
                        <span className={`inline-flex whitespace-nowrap items-center rounded-full px-2.5 py-1 text-xs font-medium ${statusStyle}`}>
                            {status || 'Open'}
                        </span>
                    );
                },
            });
        }

        if (userRole === 'customer_admin') {
            riskColumns.push({
                accessorKey: 'approval_status',
                header: 'Platform Review',
                enableSorting: false,
                cell: ({ getValue }) => {
                    const status = (getValue() || 'approved').toLowerCase();
                    const style = status === 'pending'
                        ? 'bg-amber-100 text-amber-800'
                        : status === 'rejected'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-green-100 text-green-700';
                    const label = status === 'pending'
                        ? 'Awaiting approval'
                        : status.charAt(0).toUpperCase() + status.slice(1);
                    return (
                        <span className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${style}`}>
                            {label}
                        </span>
                    );
                },
                size: 150,
            });
        }

        if (userRole === 'customer_admin' || userRole === 'security_analyst') {
            riskColumns.push({
                id: 'assign',
                header: 'Assign',
                enableSorting: false,
                cell: ({ row }) => (
                    <button
                        type="button"
                        onClick={() => handleAssign(row.original)}
                        aria-label={`Assign risk ${row.original.risk_id}`}
                        title="Assign Risk"
                        className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-primary-50 hover:text-primary-600"
                    >
                        <UserPlusIcon className="h-4 w-4" />
                    </button>
                ),
                size: 80,
            });
        }

        riskColumns.push({
            id: 'actions',
            header: 'Actions',
            enableSorting: false,
            cell: ({ row }) => {
                const risk = row.original;
                return (
                    <div className="flex items-center gap-0.5">
                        <PermissionGate permission="risk_crud" action="read">
                            <button
                                type="button"
                                onClick={() => handleViewRisk(risk)}
                                aria-label={`View risk ${risk.risk_id}`}
                                title="View"
                                className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-primary-50 hover:text-primary-600"
                            >
                                <EyeIcon className="h-4 w-4" />
                            </button>
                        </PermissionGate>
                        <PermissionGate permission="risk_crud" action="update">
                            <button
                                type="button"
                                onClick={() => handleEditRisk(risk)}
                                aria-label={`Edit risk ${risk.risk_id}`}
                                title="Edit"
                                className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-primary-50 hover:text-primary-600"
                            >
                                <PencilSquareIcon className="h-4 w-4" />
                            </button>
                        </PermissionGate>
                        <PermissionGate permission="risk_crud" action="delete">
                            <button
                                type="button"
                                onClick={() => handleDeleteRisk(risk)}
                                aria-label={`Delete risk ${risk.risk_id}`}
                                title="Delete"
                                className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                            >
                                <TrashIcon className="h-4 w-4" />
                            </button>
                        </PermissionGate>
                    </div>
                );
            },
            size: 150,
        });

        return riskColumns;
    }, [userRole, handleAssign, handleDeleteRisk, handleEditRisk, handleViewRisk, uniqueFrameworks, uniqueCategories, uniqueSubCategories]);

    const registerActions = (
        <div className="flex shrink-0 items-center gap-2">
            {(userRole === 'customer_admin' || userRole === 'security_analyst') && (
                <PermissionGate permission="risk_crud" action="create">
                    <Button variant="primary" size="sm" icon={PlusIcon} onClick={handleAddNew}>
                        New Risk
                    </Button>
                </PermissionGate>
            )}
            {userRole !== 'admin' && (
                <details className="group relative">
                    <summary className="flex h-9 cursor-pointer list-none items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand/30 [&::-webkit-details-marker]:hidden">
                        <ArrowDownTrayIcon className="h-4 w-4 text-gray-500" />
                        Export
                        <ChevronDownIcon className="h-4 w-4 text-gray-400 transition-transform group-open:rotate-180" />
                    </summary>
                    <div className="absolute right-0 z-20 mt-1 min-w-36 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                        {[
                            ['csv', 'Export CSV'],
                            ['xlsx', 'Export Excel'],
                            ['pdf', 'Export PDF'],
                        ].map(([format, label]) => (
                            <button
                                key={format}
                                type="button"
                                onClick={(event) => {
                                    onExport(format);
                                    event.currentTarget.closest('details')?.removeAttribute('open');
                                }}
                                className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </details>
            )}
        </div>
    );

    const handleAssignComplete = async (assignmentData) => {
        const response = await assignRiskApi(assignmentData);
        if (response.success) { setShowAssignModal(false); getRiskList(currentPage); }
    };

    const handleDeleteConfirmation = async () => {
        try {
            let response;
            if (userRole === 'admin') response = await deleteRiskApi(editingRisk.id);
            else if (userRole === 'customer_admin') response = await deleteCustomerRiskApi(editingRisk.id);
            if (response.success) { getRiskList(currentPage); setShowDeleteModal(false); setEditingRisk(null); }
        } catch (error) { console.error('Error deleting risk:', error); }
    };

    const handleSaveRisk = async (riskData) => {
        try {
            let response;
            if (isEditing) {
                if (userRole === 'admin') response = await updateRiskApi(riskData, editingRisk.id);
                else response = await updateCustomerRiskApi(riskData, editingRisk.id);
            } else {
                if (userRole === 'admin') response = await saveRiskApi(riskData, isEditing);
                else response = await saveCustomerRiskApi({ ...riskData, customer_master_id: customerMasterId });
            }
            if (response.success) { getRiskList(); setShowForm(false); setEditingRisk(null); }
            else {
                const error = new Error(response?.message || 'The risk could not be saved.');
                error.response = { data: response };
                throw error;
            }
        } catch (error) {
            console.error('Error saving risk:', error);
            throw error;
        }
    };

    if (showForm) return <RiskForm risk={editingRisk} onSave={handleSaveRisk} onCancel={() => { setShowForm(false); setEditingRisk(null); }} isEditing={isEditing} />;
    if (showViewModal) return <RiskViewModal isOpen={showViewModal} risk={editingRisk} onClose={() => setShowViewModal(false)} />;
    if (showDeleteModal) return <RiskDeleteConfirmationModal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} riskDelete={handleDeleteConfirmation} risk={editingRisk} />;

    return (
        <div>
            <div className="mb-4 flex min-w-0 flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                    <h2 className="flex items-center gap-2 text-xl font-bold text-gray-900">
                        <ShieldExclamationIcon className="h-6 w-6 shrink-0 text-primary-500" />
                        Risk Register
                    </h2>
                    <p className="mt-0.5 text-sm text-gray-500">
                        {showingCount} of {totalRiskCount} risks
                        {totalPages > 1 && ` • Page ${currentPage} of ${totalPages}`}
                    </p>
                </div>
                {registerActions}
            </div>

            <DataTable
                columns={columns}
                data={risks}
                getRowId={(risk) => String(risk.id)}
                loading={RiskDataloading}
                maxBodyHeight="65vh"
                emptyMessage={(
                    <div className="flex flex-col items-center justify-center text-gray-400">
                        <ShieldExclamationIcon className="mb-3 h-14 w-14 opacity-40" />
                        <p className="text-lg font-medium">No risks found</p>
                        <p className="mt-1 text-sm">Try adjusting your search or filters</p>
                    </div>
                )}
                enableSorting
                enableMultiSort={false}
                manualSorting
                sorting={sorting}
                onSortingChange={onSortingChange}
                enableGlobalFilter
                globalFilter={searchTerm}
                onGlobalFilterChange={(updater) => {
                    const nextSearch = typeof updater === 'function'
                        ? updater(searchTerm)
                        : updater;
                    setSearchTerm(nextSearch);
                    setCurrentPage(1);
                }}
                searchPlaceholder="Search risks..."
                enableColumnFilters
                manualFiltering
                columnFilters={columnFilters}
                onColumnFiltersChange={(updater) => {
                    const nextFilters = typeof updater === 'function'
                        ? updater(columnFilters)
                        : updater;
                    const values = Object.fromEntries(
                        nextFilters.map(({ id, value }) => [id, value]),
                    );
                    const textFilters = Object.fromEntries(
                        [
                            'risk_id',
                            'framework',
                            'category',
                            'risk_sub_category',
                            'risk_description',
                            'created_by',
                            'impact',
                            'likelihood',
                        ]
                            .filter((id) => values[id])
                            .map((id) => [id, values[id]]),
                    );
                    setFilterStatus(values.risk_status || '');
                    setFilterSeverity(values.risk_score || '');
                    setTextColumnFilters(textFilters);
                    setCurrentPage(1);
                }}
                enableColumnVisibility={false}
                enableColumnResizing
                manualPagination
                rowCount={totalRiskCount}
                pagination={{ pageIndex: currentPage - 1, pageSize: itemsPerPage }}
                onPaginationChange={(updater) => {
                    const currentPagination = { pageIndex: currentPage - 1, pageSize: itemsPerPage };
                    const nextPagination = typeof updater === 'function'
                        ? updater(currentPagination)
                        : updater;
                    handlePageChange(nextPagination.pageIndex + 1, nextPagination.pageSize);
                }}
                className="overflow-hidden rounded-xl shadow-2xl"
            />

            {/* AssignRiskModal — always mounted so it renders regardless of table state */}
            <AssignRiskModal
                isOpen={showAssignModal}
                onClose={() => setShowAssignModal(false)}
                risk={selectedRisk}
                onAssign={handleAssignComplete}
            />
        </div>
    );
};

export default RiskManagementList;
